/**
 * Surcharge Mismatch Exception Config
 *
 * Covers: ex-sale-ls-xero-skimmer-surcharge-mismatch
 *
 * Surcharge mismatch between LS and Xero (US).
 * Flat single-row layout with coloured Chip for mismatch type.
 */
import React from 'react'
import { Chip } from '@mui/material'
import { renderSaleLsLink, renderSaleXeroLink } from './saleLinkCells'
import ClientLinkCell from '../../../components/shared/ClientLinkCell'
import { formatCurrency, formatBusinessDate } from '../../../utils/inspectorFormatters'
import { buildIssuesColumn } from '../shared/issuesColumn'
import { PLATFORM_CONTEXT_COLUMNS } from '../shared/platformColumns'
import {
  composeRowSubtitle,
  datePart,
  moneyPart,
  textPart,
} from '../shared/rowSubtitle'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { statusPillChipProps } from '../../../components/shared/AppDrawer/chipPresets'
import { sharedSalesFilters, buildSalesFilterParams } from './sharedSalesFilters'

// ── Inspector cell renderers ───────────────────────────────────────────

const renderLsReceipt = (_value: any, row: Record<string, any>): React.ReactNode =>
  renderSaleLsLink(row)

const renderXeroInvoice = (_value: any, row: Record<string, any>): React.ReactNode =>
  renderSaleXeroLink(row)

const renderClient = (value: any, row: Record<string, any>): React.ReactNode => {
  return React.createElement(ClientLinkCell, {
    customer_id: row.customer_id,
    customer_first_name: row.customer_first_name,
    customer_last_name: row.customer_last_name,
    customer_company: row.customer_company,
    lightspeed_customer_link: row.lightspeed_customer_link,
  })
}

const renderMismatchChip = (value: any, row: Record<string, any>): React.ReactNode => {
  const notes = row.surcharge_notes || ''
  let label = notes
  let tone: 'warning' | 'error' | 'info' = 'warning'

  if (notes.includes('Missing in Xero')) {
    label = 'Missing in Xero'
    tone = 'warning'
  } else if (notes.includes('Missing in Lightspeed')) {
    label = 'Missing in Lightspeed'
    tone = 'error'
  } else if (notes.includes('Amount differs')) {
    label = 'Amount differs'
    tone = 'info'
  }

  return React.createElement(Chip, statusPillChipProps(label, tone))
}

// ── Sort helpers ───────────────────────────────────────────────────────

const getClientSortValue = (row: Record<string, any>): string => {
  const parts: string[] = []
  if (row.customer_first_name) parts.push(row.customer_first_name)
  if (row.customer_last_name) parts.push(row.customer_last_name)
  if (row.customer_company) parts.push(row.customer_company)
  return parts.length > 0 ? parts.join(' ') : row.customer_id || ''
}

// ── Column definitions ────────────────────────────────────────────────

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'invoice_number',
    label: 'Receipt #',
    contexts: ['inspector'],
    render: renderLsReceipt,
    getSortValue: (row) => row.invoice_number || '',
  },
  {
    key: 'sale_date',
    label: 'Date',
    contexts: ['inspector'],
    render: (v) => formatBusinessDate(v),
    getSortValue: (row) => row.sale_date || '',
  },
  {
    key: 'client',
    label: 'Client',
    align: 'left',
    contexts: ['inspector'],
    render: renderClient,
    getSortValue: getClientSortValue,
  },
  {
    key: 'surcharge_notes',
    label: 'Mismatch',
    contexts: ['inspector'],
    render: renderMismatchChip,
  },
  {
    key: 'surcharge_amount',
    label: 'Surcharge',
    align: 'right',
    contexts: ['inspector'],
    render: (value) => formatCurrency(value),
    getSortValue: (row) => row.surcharge_amount || 0,
  },
  {
    key: 'xero_invoice',
    label: 'Xero Invoice',
    contexts: ['inspector'],
    render: renderXeroInvoice,
  },
]

const platformColumns: ExceptionColumnDef[] = [
  ...PLATFORM_CONTEXT_COLUMNS,
  {
    key: 'invoice_number',
    label: 'LS Sale #',
    contexts: ['platform'],
    link: { field: 'lightspeed_sale_link', system: 'lightspeed' },
  },
  {
    key: 'sale_date',
    label: 'Sale Date',
    type: 'business_date',
    contexts: ['platform'],
    exportFormat: 'date',
  },
  {
    key: 'customer_name',
    label: 'Client',
    contexts: ['platform'],
  },
  {
    key: 'surcharge_amount',
    label: 'Amount',
    type: 'currency',
    align: 'right',
    contexts: ['platform'],
    exportFormat: 'currency',
  },
  {
    key: 'surcharge_notes',
    label: 'Notes',
    contexts: ['platform'],
  },
  {
    key: 'xero_reference',
    label: 'Xero Inv. #',
    contexts: ['platform'],
    link: { field: 'xero_invoice_link', system: 'xero' },
  },
  {
    key: 'exception_type',
    label: 'Exception Type',
    contexts: ['platform'],
  },
]

export const surchargeConfig: ExceptionConfig = {
  codes: ['ex-sale-ls-xero-skimmer-surcharge-mismatch'],
  columns: [buildIssuesColumn(), ...inspectorColumns, ...platformColumns],
  defaultSortColumn: 'sale_date',
  defaultSortDirection: 'desc',
  complexity: 'simple',
  // Canonical tag id keys the row (unique + stable); backend returns one
  // canonical row per object - no JS dedupe.
  rowKeyField: 'exception_tag_id',
  getRowSubtitle: row =>
    composeRowSubtitle([
      textPart(row.customer_name) || 'Walk-in',
      datePart(row.sale_date),
      moneyPart(row.total_amount_incl_tax ?? row.ls_total_amount),
    ]),
  rowDetail: {
    type: 'compare-sales',
  },
  filters: [...sharedSalesFilters],
  buildFetchParams: (urlState) => buildSalesFilterParams(urlState.filterValues),
}
